import { invokeFunction, supabase } from '@/integrations/supabase/client';
import type { InventorySourceValue } from '@/shared/inventoryTabSources';
import { readFunctionErrorBody } from '@/utils/functionErrorBody';

// Saves the merchant's Inventory source (Settings › Reports and Charts › Data
// sources, FIG-442 · UC-251) through the admin-only edge function. The function
// writes the setting, queues the report regeneration and records the change.

export interface SaveInventorySourceResult {
  ok: boolean;
  /** True when the chosen source was already the saved one and nothing was done. */
  unchanged?: boolean;
  /** False when the setting was saved but its history entry wasn't. */
  recorded?: boolean;
  /** Set when the setting was saved but the report regeneration could not be queued or started. */
  regenerationError?: string;
  error?: string;
}

interface FunctionResponse {
  success?: boolean;
  unchanged?: boolean;
  recorded?: boolean;
  regeneration?: { queued?: number; error?: string };
  error?: string;
}

const FALLBACK_ERROR = 'Could not save the inventory source';

export async function saveInventorySource(
  organisationId: string,
  source: InventorySourceValue,
): Promise<SaveInventorySourceResult> {
  try {
    const { data, error } = await invokeFunction<FunctionResponse>(supabase, 'update-merchant-inventory-source-v1', {
      body: { organisationId, source },
    });
    if (error || !data?.success) {
      return { ok: false, error: data?.error ?? (error instanceof Error ? error.message : FALLBACK_ERROR) };
    }
    return {
      ok: true,
      unchanged: data.unchanged === true,
      recorded: data.recorded !== false,
      regenerationError: data.regeneration?.error,
    };
  } catch (error) {
    // A non-2xx response is thrown; its JSON body is only on the error's context.
    const body = await readFunctionErrorBody<FunctionResponse>(error);
    return { ok: false, error: body?.error ?? (error instanceof Error ? error.message : FALLBACK_ERROR) };
  }
}
